/**
 * 案件モードの画面が「マスターにも同じ節点が居るか」を、案件の変更の行から決める。
 *
 * **案件モードの画面はマスターの姿を必ず引いている。** 左右比較の変更前（業務詳細の図・
 * 業務フローの図）と、案件モードを外す導線の行き先がそれである。ところが案件が新設した
 * 節点にはマスターの姿が無いので、引けば必ず未存在で返る。**問い合わせてから未存在と知る形は、
 * 押していない利用者にも 404 を 1 本ずつ積む**（ブラウザのコンソールと通信の記録に失敗として
 * 残り、本物の失敗と見分けが付かなくなる）。外す導線に至っては、押すと「見つかりません」に
 * 着く。
 *
 * 案件が新設したかどうかは、画面が既に取っている案件の変更の行（`getChangeHistory`）に
 * 書いてある — 変更前が無い行が「その案件が作った」である（`pjChangeDiffState`）。同じ取得を
 * 見るので、問い合わせは 1 本も増えない。
 *
 * **判定を画面ごとに書き写さない。** 3 箇所（業務詳細・案件から見たフロー・案件モードを
 * 外す導線）が同じ問いを持つので、片方だけ条件を変えると経路によって 404 が出たり出なかったり
 * する。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 案件が新設した節点かどうかを案件の変更の行から読み、マスターの姿を引いてよいかを決める。
 * @graph-connects pjChangeDiffState [calls] 案件の変更 1 行の差分の状態
 * @graph-connects usePjChanges [calls] 案件の変更の行の取得
 */
import type { PjChangeRow } from '@cortex/domain-spec-console';

import { usePjChanges } from '../hooks/queries/spec-graph-element-pj-queries.js';
import { pjChangeDiffState } from './pj-touched-index.js';

/**
 * マスターにその節点が居るか。
 *
 * - `unknown`: 案件の変更の行がまだ返っていない。**引いてよいとも悪いとも言わない**
 * - `absent`: その案件が新設した節点なので、マスターには居ない
 * - `present`: マスターにも居る（案件が触っていない、更新した、削除した）
 */
export type PjMasterPresence = 'unknown' | 'absent' | 'present';

/**
 * 案件の変更の行から、マスターにその節点が居るかを決める。
 *
 * **行が無いことは「マスターに居る」である。** 案件が触っていない節点は行を持たない
 * （設計書 §5 手順 5）。触っていないのだからマスターの姿はそのまま在る。
 *
 * 削除の行も「マスターに居る」である。消すのは案件の側であって、マスターにはまだ在る。
 * 変更前の姿を引く画面は、まさにその消える前の姿を見せるために引いている。
 *
 * @param rows 案件の変更の行。まだ返っていなければ undefined
 * @param nodeId 調べる節点の番号
 * @returns マスターにその節点が居るか
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 案件の変更の行から、その節点がマスターにも居るか・案件が新設したものかを決める。
 * @graph-connects pjChangeDiffState [calls] 案件の変更 1 行の差分の状態
 */
export function pjMasterPresenceOf(
  rows: readonly PjChangeRow[] | undefined,
  nodeId: number,
): PjMasterPresence {
  if (rows === undefined) return 'unknown';
  const row = rows.find((candidate) => candidate.nodeId === nodeId);
  if (row === undefined) return 'present';
  return pjChangeDiffState(row) === 'created' ? 'absent' : 'present';
}

/**
 * 案件を重ねた姿にその節点が居るか。
 *
 * - `unknown`: 案件の変更の行がまだ返っていない。**引いてよいとも悪いとも言わない**
 * - `absent`: その案件が消す節点なので、案件を重ねた姿には居ない
 * - `present`: 案件を重ねた姿にも居る（案件が触っていない、作った、更新した）
 */
export type PjAppliedPresence = 'unknown' | 'absent' | 'present';

/**
 * 案件の変更の行から、案件を重ねた姿にその節点が居るかを決める。
 *
 * マスターに居るか（`pjMasterPresenceOf`）の裏返しの問いである。**案件が消す節点を重ねた姿で
 * 引くと必ず未存在で返る**ので、変更後を引く画面（業務詳細・案件から見たフロー）は、
 * 消すと分かっていれば引かずに、変更前と変更後に無いことを並べた削除の画面を描く。
 *
 * @param rows 案件の変更の行。まだ返っていなければ undefined
 * @param nodeId 調べる節点の番号
 * @returns 案件を重ねた姿にその節点が居るか
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 案件の変更の行から、その節点が案件を重ねた姿にも居るか・案件が消すものかを決める。
 * @graph-connects pjChangeDiffState [calls] 案件の変更 1 行の差分の状態
 */
export function pjAppliedPresenceOf(
  rows: readonly PjChangeRow[] | undefined,
  nodeId: number,
): PjAppliedPresence {
  if (rows === undefined) return 'unknown';
  const row = rows.find((candidate) => candidate.nodeId === nodeId);
  if (row === undefined) return 'present';
  return pjChangeDiffState(row) === 'deleted' ? 'absent' : 'present';
}

/** マスターと案件を重ねた姿のそれぞれに、その節点が居るか。 @graph-connects none */
export interface PjNodePresence {
  /** マスターに居るか（案件が作ったなら居ない）。 */
  master: PjMasterPresence;
  /** 案件を重ねた姿に居るか（案件が消すなら居ない）。 */
  applied: PjAppliedPresence;
}

/**
 * いま重ねている案件の変更の行から、マスターと案件を重ねた姿の両方について節点が居るかを読む。
 *
 * **2 つの問いを 1 回の取得から答える。** 変更前と変更後の両方を引く画面（業務詳細）は
 * 両方の答えが要る。別々のフックにすると、同じ行を 2 箇所で読んで片方だけ条件を変える余地が
 * 生まれる。呼ぶのは案件モードに居ることが確定している場所だけである（`usePjMasterPresence`
 * と同じ）。
 *
 * @param pjNumber いま重ねている案件番号
 * @param nodeId 調べる節点の番号
 * @returns マスターと案件を重ねた姿のそれぞれに居るか
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business いま重ねている案件の変更の行を引き、その節点がマスターと案件を重ねた姿のそれぞれに居るかを読む。
 * @graph-connects usePjChanges [calls] 案件の変更の行の取得
 * @graph-connects pjMasterPresenceOf [calls] マスターに居るかの判定
 * @graph-connects pjAppliedPresenceOf [calls] 案件を重ねた姿に居るかの判定
 */
export function usePjNodePresence(pjNumber: number, nodeId: number): PjNodePresence {
  const rows = usePjChanges(pjNumber).data?.changes;
  return { master: pjMasterPresenceOf(rows, nodeId), applied: pjAppliedPresenceOf(rows, nodeId) };
}

/**
 * いま重ねている案件の変更の行から、マスターにその節点が居るかを読む。
 *
 * **呼ぶのは案件モードに居ることが確定している場所だけである。** 案件の変更の行の取得を
 * 抱えるので、案件を選んでいない画面から呼ぶと案件番号の無い問い合わせが飛ぶ。呼び出す 3 箇所は
 * どれも案件が確定した後の部品に分けてある。
 *
 * @param pjNumber いま重ねている案件番号
 * @param nodeId 調べる節点の番号
 * @returns マスターにその節点が居るか
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business いま重ねている案件の変更の行を引き、その節点がマスターにも居るかを読む。
 * @graph-connects usePjChanges [calls] 案件の変更の行の取得
 * @graph-connects pjMasterPresenceOf [calls] 変更の行からの判定
 */
export function usePjMasterPresence(pjNumber: number, nodeId: number): PjMasterPresence {
  const changes = usePjChanges(pjNumber);
  return pjMasterPresenceOf(changes.data?.changes, nodeId);
}
